import { shuffleDeterministically } from "../random/seeded-random";
import {
  MAX_HAND_SIZE,
  type CardInstanceId,
  type GameEvent,
  type PlayerId,
  type SeededRandomState,
  type SetupGameState,
} from "../types";

export interface DrawCardsResult {
  readonly state: SetupGameState;
  readonly events: readonly GameEvent[];
  readonly drawnCardInstanceIds: readonly CardInstanceId[];
}

function eventId(actionSequence: number, ordinal: number): string {
  return `event-${actionSequence}-${ordinal}`;
}

/**
 * Draws from array index zero one card at a time. The discard pile is shuffled
 * only at the precise point a requested draw finds an empty deck.
 */
export function drawCards(
  state: Readonly<SetupGameState>,
  playerId: PlayerId,
  requestedCount: number,
  actionSequence: number,
  startingEventOrdinal = 0,
): DrawCardsResult {
  if (!Number.isSafeInteger(requestedCount) || requestedCount < 0) {
    throw new RangeError("requestedCount must be a non-negative safe integer");
  }

  const player = state.players[playerId];
  const drawableCount =
    state.schemaVersion === 5
      ? Math.min(requestedCount, Math.max(0, MAX_HAND_SIZE - player.hand.length))
      : requestedCount;
  let deck = [...player.deck];
  let discard = [...player.discard];
  let random: SeededRandomState = state.random;
  const drawn: CardInstanceId[] = [];
  const events: GameEvent[] = [];

  if (state.schemaVersion === 5 && drawableCount < requestedCount) {
    events.push({
      type: "hand-limit-reached",
      eventId: eventId(actionSequence, startingEventOrdinal + events.length),
      actionSequence,
      playerId,
      requestedCount,
      drawableCount,
      handSizeBefore: player.hand.length,
      maximumHandSize: MAX_HAND_SIZE,
    });
  }

  while (drawn.length < drawableCount) {
    if (deck.length === 0) {
      if (discard.length === 0) break;

      const recycled = shuffleDeterministically(discard, random);
      deck = [...recycled.value];
      discard = [];
      random = recycled.state;
      events.push({
        type: "deck-recycled",
        eventId: eventId(actionSequence, startingEventOrdinal + events.length),
        actionSequence,
        playerId,
        cardInstanceIds: deck,
      });
    }

    const cardInstanceId = deck.shift();
    if (cardInstanceId === undefined) break;
    drawn.push(cardInstanceId);
  }

  events.push({
    type: "cards-drawn",
    eventId: eventId(actionSequence, startingEventOrdinal + events.length),
    actionSequence,
    playerId,
    requestedCount,
    cardInstanceIds: drawn,
  });

  if (drawn.length < drawableCount) {
    events.push({
      type: "draw-shortfall",
      eventId: eventId(actionSequence, startingEventOrdinal + events.length),
      actionSequence,
      playerId,
      requestedCount,
      drawnCount: drawn.length,
    });
  }

  return {
    state: {
      ...state,
      random,
      players: {
        ...state.players,
        [playerId]: {
          ...player,
          deck,
          hand: [...player.hand, ...drawn],
          discard,
        },
      },
    },
    events,
    drawnCardInstanceIds: drawn,
  };
}
